import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusBadge from "@/components/common/StatusBadge";
import DocumentTable from "@/components/common/DocumentTable";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { format } from "date-fns";
import { Search, FileText, Award, BarChart3 } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import ExamResultDetail from "@/components/common/ExamResultDetail";
import CertificateDownload from "@/components/common/CertificateDownload";
import InlineCertificateViewer from "@/components/common/InlineCertificateViewer";
import { LetterDownload, InlineLetterViewer } from "@/components/common/LetterOfGoodStanding";
import { memberNumberOf } from "@/lib/membershipNumber";

const AVATAR_COLORS = ["#6d28d9", "#4f46e5", "#0e7490", "#b45309", "#15803d", "#be185d"];
const avatarColor = (s: string) => AVATAR_COLORS[[...(s || "?")].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
const initials = (name: string) => (name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

const ApprovedList = () => {
  useAuth();
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [docsApp, setDocsApp] = useState<any>(null);
  const [resultApp, setResultApp] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [examMap, setExamMap] = useState<Record<string, any[]>>({});

  const fetchApps = async () => {
    const { data } = await supabase
      .from("applications")
      .select("*")
      .in("status", ["approved", "paid" as any, "certified"])
      .order("updated_at", { ascending: false });
    const apps = data || [];
    setApplications(apps);

    // Load exam assignments for all these students
    if (apps.length > 0) {
      const userIds = apps.map((a: any) => a.user_id);
      const { data: exams } = await supabase
        .from("exam_assignments")
        .select("*")
        .in("user_id", userIds);
      const map: Record<string, any[]> = {};
      for (const e of exams || []) {
        if (!map[e.user_id]) map[e.user_id] = [];
        map[e.user_id].push(e);
      }
      setExamMap(map);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchApps();
  }, []);

  const viewDocs = async (app: any) => {
    setDocsApp(app);
    setDocsLoading(true);
    const { data } = await supabase
      .from("documents")
      .select("*")
      .eq("application_id", app.id);
    setDocuments(data || []);
    setDocsLoading(false);
  };

  const filtered = applications.filter(
    (app) =>
      app.full_name.toLowerCase().includes(search.toLowerCase()) ||
      app.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <PageHeader
          title="Approved Students"
          badge={
            filtered.length > 0 ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
                {filtered.length} members
              </span>
            ) : undefined
          }
          crumbLabel="Dashboard"
          crumbTo="/aca"
          actions={
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-11 rounded-xl pl-10"
              />
            </div>
          }
        />

        {/* Table */}
        <Card className="glass-premium overflow-hidden rounded-2xl">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : (
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Name / Email</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Qualification</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Exam</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Date</TableHead>
                    <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</TableHead>
                    <TableHead className="text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Certificate &amp; Letter</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((app) => (
                    <TableRow key={app.id} className="border-border hover:bg-muted/40 transition-colors">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-bold text-white" style={{ background: avatarColor(app.full_name) }}>
                            {initials(app.full_name)}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground">{app.full_name}</p>
                            <p className="text-xs text-muted-foreground">{app.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-foreground">{app.qualification || "—"}</TableCell>
                      <TableCell>
                        {(examMap[app.user_id] || []).length > 0 ? (
                          <div className="flex flex-col gap-0.5">
                            {(examMap[app.user_id] || []).map((ea: any) => (
                              <span key={ea.id} className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${ea.status === "passed" ? "bg-green-100 text-green-700" : ea.status === "failed" ? "bg-red-100 text-red-700" : "bg-muted text-muted-foreground"}`}>
                                {ea.think_exam_test_name || ea.think_exam_test_id}: {ea.status}
                              </span>
                            ))}
                          </div>
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(app.updated_at), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={app.payment_status === "paid" ? "paid" : app.status} />
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          {(examMap[app.user_id] || []).length > 0 && (
                            <Button size="sm" variant="ghost" onClick={() => setResultApp(app)} className="hover:bg-primary/10">
                              <BarChart3 className="mr-1 h-3.5 w-3.5" />
                              Result
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => viewDocs(app)} className="hover:bg-primary/10">
                            <FileText className="mr-1 h-3.5 w-3.5" />
                            Docs
                          </Button>
                          {(app.status === "certified" || app.payment_status === "paid") && (
                            <>
                              <InlineCertificateViewer
                                fullName={app.full_name}
                                certId={memberNumberOf(app)}
                                date={format(new Date(app.updated_at), "MMMM d, yyyy")}
                              />
                              <CertificateDownload
                                fullName={app.full_name}
                                certId={memberNumberOf(app)}
                                date={format(new Date(app.updated_at), "MMMM d, yyyy")}
                                applicationId={app.id}
                              />
                              <InlineLetterViewer
                                fullName={app.full_name}
                                memberNum={memberNumberOf(app)}
                                date={format(new Date(app.updated_at), "MMMM d, yyyy")}
                                city={app.city}
                                country={app.country}
                                gender={app.gender}
                              />
                              <LetterDownload
                                fullName={app.full_name}
                                memberNum={memberNumberOf(app)}
                                date={format(new Date(app.updated_at), "MMMM d, yyyy")}
                                city={app.city}
                                country={app.country}
                                gender={app.gender}
                              />
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                        <Award className="mx-auto h-8 w-8 text-muted-foreground/30 mb-2" />
                        No approved students yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Docs Dialog */}
      <Dialog open={!!docsApp} onOpenChange={() => setDocsApp(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Documents — {docsApp?.full_name}
            </DialogTitle>
          </DialogHeader>
          {docsLoading ? (
            <div className="flex justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <DocumentTable documents={documents} />
          )}
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={!!resultApp} onOpenChange={() => setResultApp(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0">
          <DialogHeader className="border-b border-border px-6 py-4">
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Exam Results — {resultApp?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[70vh] px-6 py-5 space-y-3">
            {resultApp && (examMap[resultApp.user_id] || []).length > 0 ? (
              (examMap[resultApp.user_id] || []).map((ea: any) => (
                <div key={ea.id} className={`rounded-xl border p-4 ${ea.status === "passed" ? "border-green-200 bg-green-50/50 dark:bg-green-950/10" : ea.status === "failed" ? "border-red-200 bg-red-50/50 dark:bg-red-950/10" : "border-border/50 bg-muted/10"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{ea.think_exam_test_name || ea.think_exam_test_id}</p>
                      <p className="text-xs text-muted-foreground">{ea.think_exam_group_name}</p>
                      {ea.score != null && (
                        <p className="text-xs mt-1 font-medium">Score: {ea.score}/{ea.total_marks} ({ea.percentage}%)</p>
                      )}
                    </div>
                    <span className={`shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${ea.status === "passed" ? "bg-green-100 text-green-700" : ea.status === "failed" ? "bg-red-100 text-red-700" : "bg-muted text-muted-foreground"}`}>
                      {ea.status === "passed" ? "Passed" : ea.status === "failed" ? "Failed" : "Pending"}
                    </span>
                  </div>
                  {(ea.status === "passed" || ea.status === "failed") && <ExamResultDetail exam={ea} />}
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center py-8 text-center text-muted-foreground">
                <BarChart3 className="h-7 w-7 text-muted-foreground/30 mb-2" />
                <p className="text-sm">No exam results for this student.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ApprovedList;
